import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LogOut, RefreshCw, Wallet, ArrowDownToLine, Send, TrendingUp, ArrowRightLeft, Trash2, Copy, Bell, Key, ChevronLeft, ChevronRight, UserPlus, Users, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Notification sound - Cash Register
const NOTIFICATION_SOUND = "/notification.mp3";

function AdminDashboard() {
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const [currencies, setCurrencies] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("requests");
  const [editingRates, setEditingRates] = useState({});
  const [balances, setBalances] = useState({});
  const [bybitBalance, setBybitBalance] = useState(null);
  const [bybitPrices, setBybitPrices] = useState([]);
  const [bybitLoading, setBybitLoading] = useState(false);
  const [bybitAddresses, setBybitAddresses] = useState([]);
  const [depositCheckerStatus, setDepositCheckerStatus] = useState(null);
  const [commissionPercent, setCommissionPercent] = useState(2);
  const [newCommission, setNewCommission] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [searchWallet, setSearchWallet] = useState("");
  const [visibleContacts, setVisibleContacts] = useState({});
  const [autoExchangeEnabled, setAutoExchangeEnabled] = useState(false);
  const [requestsPage, setRequestsPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const REQUESTS_PER_PAGE = 10;
  const [exchangeProvider, setExchangeProvider] = useState("bybit");
  const [whitebitConfigured, setWhitebitConfigured] = useState(false);
  const [whitebitBalance, setWhitebitBalance] = useState(null);
  const [bybitApiKey, setBybitApiKey] = useState("");
  const [bybitApiSecret, setBybitApiSecret] = useState("");
  const [bybitKeysConfigured, setBybitKeysConfigured] = useState(false);
  const [showBybitKeys, setShowBybitKeys] = useState(false);
  
  // USDT/USDT Bybit keys state
  const [bybitApiKeyUsdt, setBybitApiKeyUsdt] = useState("");
  const [bybitApiSecretUsdt, setBybitApiSecretUsdt] = useState("");
  const [bybitKeysUsdtConfigured, setBybitKeysUsdtConfigured] = useState(false);
  const [showBybitKeysUsdt, setShowBybitKeysUsdt] = useState(false);
  
  // USDT/USDT fixed fee
  const [usdtFee, setUsdtFee] = useState(3.0);
  const [editUsdtFee, setEditUsdtFee] = useState("");
  
  // User management state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [allowSameCurrencyExchange, setAllowSameCurrencyExchange] = useState(false);
  
  // User Bybit API keys modal state
  const [showUserBybitModal, setShowUserBybitModal] = useState(false);
  const [selectedUserForBybit, setSelectedUserForBybit] = useState(null);
  const [userBybitApiKey, setUserBybitApiKey] = useState("");
  const [userBybitApiSecret, setUserBybitApiSecret] = useState("");
  const [userBybitConfigured, setUserBybitConfigured] = useState(false);
  
  // User Bybit Analytics state
  const [userBybitAnalytics, setUserBybitAnalytics] = useState(null);
  const [showBybitAnalytics, setShowBybitAnalytics] = useState(false);
  const [bybitAnalyticsLoading, setBybitAnalyticsLoading] = useState(false);

  // Operator management state
  const [operators, setOperators] = useState([]);
  const [newOperatorUsername, setNewOperatorUsername] = useState("");
  const [newOperatorPassword, setNewOperatorPassword] = useState("");
  const [showAddOperator, setShowAddOperator] = useState(false);

  // Chat management state
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatReply, setChatReply] = useState("");
  const [chatsLoading, setChatsLoading] = useState(false);

  // Toggle contact visibility
  const toggleContactVisibility = (requestId) => {
    setVisibleContacts(prev => ({
      ...prev,
      [requestId]: !prev[requestId]
    }));
  };

  // Copy to clipboard helper
  const copyToClipboard = (text, label = "Скопировано") => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  // Status filter options
  const STATUS_FILTERS = useMemo(() => [
    { value: "all", label: "Все", color: "bg-gray-600" },
    { value: "pending", label: "Ожидание", color: "bg-yellow-500" },
    { value: "awaiting_confirmation", label: "Ожид. подтв.", color: "bg-orange-500" },
    { value: "in_progress", label: "В работе", color: "bg-blue-500" },
    { value: "completed", label: "Завершено", color: "bg-green-500" },
    { value: "cancelled", label: "Отменено", color: "bg-red-500" },
  ], []);

  // Filter requests by wallet and status (memoized)
  const filteredRequests = useMemo(() => {
    return requests.filter(r => {
      // Status filter
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      
      // Search filter
      if (searchWallet.trim()) {
        const search = searchWallet.toLowerCase();
        return (
          r.user_wallet?.toLowerCase().includes(search) ||
          r.deposit_address?.toLowerCase().includes(search) ||
          r.user_email?.toLowerCase().includes(search) ||
          r.order_number?.toLowerCase().includes(search)
        );
      }
      return true;
    });
  }, [requests, statusFilter, searchWallet]);
  
  // Status counts for filter badges (memoized)
  const statusCounts = useMemo(() => {
    const counts = { all: requests.length };
    requests.forEach(r => {
      counts[r.status] = (counts[r.status] || 0) + 1;
    });
    return counts;
  }, [requests]);

  // Pagination for requests
  const totalRequestsPages = Math.ceil(filteredRequests.length / REQUESTS_PER_PAGE);
  const paginatedRequests = filteredRequests.slice(
    (requestsPage - 1) * REQUESTS_PER_PAGE,
    requestsPage * REQUESTS_PER_PAGE
  );

  // Reset page when search or filter changes
  useEffect(() => {
    setRequestsPage(1);
  }, [searchWallet, statusFilter]);

  // Play notification sound
  const playNotificationSound = () => {
    try {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(e => console.log("Audio play failed:", e));
      }
    } catch (e) {
      console.log("Audio error:", e);
    }
  };

  // Change password handler
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("Пароли не совпадают");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Пароль должен быть минимум 6 символов");
      return;
    }
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success("Пароль успешно изменён");
      setShowPasswordModal(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка смены пароля");
    }
  };

  const fetchCommission = async () => {
    try {
      const response = await axios.get(`${API}/settings/commission`);
      setCommissionPercent(response.data.commission_percent);
      setNewCommission(response.data.commission_percent.toString());
    } catch (error) {
      console.error("Error fetching commission:", error);
    }
  };

  const fetchAutoExchangeSetting = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/auto-exchange`);
      setAutoExchangeEnabled(response.data.auto_exchange_enabled);
    } catch (error) {
      console.error("Error fetching auto-exchange setting:", error);
    }
  };

  const toggleAutoExchange = async () => {
    try {
      const newValue = !autoExchangeEnabled;
      await axios.put(`${API}/admin/settings/auto-exchange?enabled=${newValue}`);
      setAutoExchangeEnabled(newValue);
      toast.success(newValue ? "Автообмен включён" : "Автообмен выключен");
    } catch (error) {
      toast.error("Ошибка изменения настройки");
    }
  };

  const fetchExchangeProvider = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/exchange-provider`);
      setExchangeProvider(response.data.provider);
      setWhitebitConfigured(response.data.whitebit_configured);
    } catch (error) {
      console.error("Error fetching exchange provider:", error);
    }
  };

  const switchExchangeProvider = async (provider) => {
    try {
      await axios.put(`${API}/admin/settings/exchange-provider?provider=${provider}`);
      setExchangeProvider(provider);
      toast.success(`Биржа переключена на ${provider.toUpperCase()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка переключения биржи");
    }
  };

  const fetchWhitebitBalance = async () => {
    try {
      const response = await axios.get(`${API}/admin/whitebit/balance`);
      setWhitebitBalance(response.data);
      toast.success("Баланс WhiteBIT загружен");
    } catch (error) {
      toast.error("Ошибка загрузки баланса WhiteBIT");
    }
  };

  const fetchBybitKeys = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/bybit-keys`);
      setBybitKeysConfigured(response.data.configured);
    } catch (error) {
      console.error("Error fetching Bybit keys:", error);
    }
  };

  const updateBybitKeys = async () => {
    if (!bybitApiKey || !bybitApiSecret) {
      toast.error("Введите API ключ и секрет");
      return;
    }
    try {
      await axios.put(`${API}/admin/settings/bybit-keys?api_key=${encodeURIComponent(bybitApiKey)}&api_secret=${encodeURIComponent(bybitApiSecret)}`);
      toast.success("Bybit API ключи обновлены");
      setBybitApiKey("");
      setBybitApiSecret("");
      setShowBybitKeys(false);
      setBybitKeysConfigured(true);
      fetchBybitKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка обновления ключей");
    }
  };

  // USDT/USDT Bybit keys functions
  const fetchBybitKeysUsdt = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/bybit-keys-usdt`);
      setBybitKeysUsdtConfigured(response.data.configured);
    } catch (error) {
      console.error("Error fetching USDT Bybit keys:", error);
    }
  };

  const updateBybitKeysUsdt = async () => {
    if (!bybitApiKeyUsdt || !bybitApiSecretUsdt) {
      toast.error("Введите API ключ и секрет");
      return;
    }
    try {
      await axios.put(`${API}/admin/settings/bybit-keys-usdt?api_key=${encodeURIComponent(bybitApiKeyUsdt)}&api_secret=${encodeURIComponent(bybitApiSecretUsdt)}`);
      toast.success("Bybit API ключи для USDT/USDT обновлены");
      setBybitApiKeyUsdt("");
      setBybitApiSecretUsdt("");
      setShowBybitKeysUsdt(false);
      setBybitKeysUsdtConfigured(true);
      fetchBybitKeysUsdt();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка обновления ключей");
    }
  };

  // Fetch users for management
  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setUsersLoading(false);
    }
  };

  // Fetch USDT/USDT fixed fee
  const fetchUsdtCommission = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/usdt-commission`);
      setUsdtFee(response.data.usdt_fixed_fee);
    } catch (error) {
      console.error("Error fetching USDT fee:", error);
    }
  };

  // Update USDT/USDT fixed fee
  const updateUsdtCommission = async () => {
    const value = parseFloat(editUsdtFee);
    if (isNaN(value) || value < 0) {
      toast.error("Введите корректное значение");
      return;
    }
    try {
      await axios.put(`${API}/admin/settings/usdt-commission?fee=${value}`);
      setUsdtFee(value);
      setEditUsdtFee("");
      toast.success(`Фикс комиссия USDT/USDT: $${value}`);
    } catch (error) {
      toast.error("Ошибка обновления");
    }
  };

  // Fetch same currency exchange setting
  const fetchSameCurrencyExchangeSetting = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/same-currency-exchange`);
      setAllowSameCurrencyExchange(response.data.allow_same_currency_exchange);
    } catch (error) {
      console.error("Error fetching same currency setting:", error);
    }
  };

  // Toggle same currency exchange setting
  const toggleSameCurrencyExchange = async () => {
    try {
      const newValue = !allowSameCurrencyExchange;
      await axios.put(`${API}/admin/settings/same-currency-exchange?enabled=${newValue}`);
      setAllowSameCurrencyExchange(newValue);
      toast.success(newValue ? "Обмен одинаковых валют разрешён" : "Обмен одинаковых валют запрещён");
    } catch (error) {
      toast.error("Ошибка обновления настройки");
    }
  };

  // Operator management functions
  const fetchOperators = async () => {
    try {
      const response = await axios.get(`${API}/admin/operators`);
      setOperators(response.data);
    } catch (error) {
      console.error("Error fetching operators:", error);
    }
  };

  const createOperator = async () => {
    if (!newOperatorUsername || !newOperatorPassword) {
      toast.error("Заполните все поля");
      return;
    }
    try {
      await axios.post(`${API}/admin/operators`, {
        username: newOperatorUsername,
        password: newOperatorPassword
      });
      toast.success(`Оператор ${newOperatorUsername} создан`);
      setNewOperatorUsername("");
      setNewOperatorPassword("");
      setShowAddOperator(false);
      fetchOperators();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка создания оператора");
    }
  };

  const deleteOperator = async (operatorId) => {
    if (!window.confirm("Удалить оператора?")) return;
    try {
      await axios.delete(`${API}/admin/operators/${operatorId}`);
      toast.success("Оператор удалён");
      fetchOperators();
    } catch (error) {
      toast.error("Ошибка удаления оператора");
    }
  };

  const assignRequestToOperator = async (requestId, operatorUsername) => {
    try {
      await axios.post(`${API}/admin/requests/${requestId}/assign?operator_username=${operatorUsername}`);
      toast.success(`Заявка назначена ${operatorUsername}`);
      fetchRequests();
    } catch (error) {
      toast.error("Ошибка назначения заявки");
    }
  };

  // Chat management functions
  const fetchChats = async () => {
    setChatsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/chats`);
      setChats(response.data || []);
      // Update selected chat if it exists
      if (selectedChat) {
        const updated = response.data.find(c => c.session_id === selectedChat.session_id);
        if (updated) {
          setSelectedChat(updated);
        }
      }
    } catch (error) {
      console.error("Error fetching chats:", error);
    } finally {
      setChatsLoading(false);
    }
  };

  // Auto-refresh chats every 3 seconds when on chats tab
  useEffect(() => {
    let interval;
    if (selectedChat) {
      interval = setInterval(() => {
        fetchChats();
      }, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [selectedChat?.session_id]);

  const sendChatReply = async () => {
    if (!chatReply.trim() || !selectedChat) return;
    const messageText = chatReply.trim();
    setChatReply("");
    
    try {
      await axios.post(`${API}/admin/chat/reply`, null, {
        params: {
          session_id: selectedChat.session_id,
          message: messageText
        }
      });
      toast.success("Сообщение отправлено");
      // Immediately refresh to show new message
      fetchChats();
    } catch (error) {
      toast.error("Ошибка отправки сообщения");
      setChatReply(messageText); // Restore message on error
    }
  };

  const closeChat = async (sessionId) => {
    try {
      await axios.put(`${API}/admin/chat/${sessionId}/close`);
      toast.success("Чат закрыт");
      setSelectedChat(null);
      fetchChats();
    } catch (error) {
      toast.error("Ошибка закрытия чата");
    }
  };

  const deleteChat = async (sessionId) => {
    if (!window.confirm("Вы уверены, что хотите удалить этот чат? Это действие нельзя отменить.")) return;
    try {
      await axios.delete(`${API}/admin/chat/${sessionId}`);
      toast.success("Чат удалён");
      setSelectedChat(null);
      fetchChats();
    } catch (error) {
      toast.error("Ошибка удаления чата");
    }
  };

  // Delete user
  const deleteUser = async (userId) => {
    if (!window.confirm("Вы уверены, что хотите удалить этого пользователя?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("Пользователь удалён");
      fetchUsers();
    } catch (error) {
      toast.error("Ошибка удаления пользователя");
    }
  };

  // Update user KYC status
  const updateUserKyc = async (userId, verified) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/kyc?verified=${verified}`);
      toast.success(verified ? "Пользователь верифицирован" : "Верификация отменена");
      fetchUsers();
    } catch (error) {
      toast.error("Ошибка обновления KYC");
    }
  };

  // Toggle user's same currency exchange permission
  const toggleUserSameCurrency = async (userId, currentValue) => {
    try {
      const newValue = !currentValue;
      await axios.put(`${API}/admin/users/${userId}/same-currency?allowed=${newValue}`);
      toast.success(newValue ? "USDT/USDT разрешён" : "USDT/USDT запрещён");
      fetchUsers();
    } catch (error) {
      toast.error("Ошибка обновления настройки");
    }
  };

  // Open user Bybit keys modal
  const openUserBybitModal = async (user) => {
    setSelectedUserForBybit(user);
    setUserBybitApiKey("");
    setUserBybitApiSecret("");
    try {
      const response = await axios.get(`${API}/admin/users/${user.id}/bybit-keys`);
      setUserBybitConfigured(response.data.configured);
    } catch (error) {
      setUserBybitConfigured(false);
    }
    setShowUserBybitModal(true);
  };

  // Save user Bybit keys
  const saveUserBybitKeys = async () => {
    if (!userBybitApiKey || !userBybitApiSecret) {
      toast.error("Заполните оба поля");
      return;
    }
    try {
      await axios.put(`${API}/admin/users/${selectedUserForBybit.id}/bybit-keys?api_key=${encodeURIComponent(userBybitApiKey)}&api_secret=${encodeURIComponent(userBybitApiSecret)}`);
      toast.success("Bybit API ключи сохранены для пользователя");
      setShowUserBybitModal(false);
      fetchUsers();
    } catch (error) {
      toast.error("Ошибка сохранения ключей");
    }
  };

  // Delete user Bybit keys
  const deleteUserBybitKeys = async () => {
    if (!window.confirm("Удалить Bybit API ключи у этого пользователя?")) return;
    try {
      await axios.delete(`${API}/admin/users/${selectedUserForBybit.id}/bybit-keys`);
      toast.success("Bybit API ключи удалены");
      setUserBybitConfigured(false);
      setShowUserBybitModal(false);
      fetchUsers();
    } catch (error) {
      toast.error("Ошибка удаления ключей");
    }
  };

  // Fetch User Bybit Analytics
  const fetchUserBybitAnalytics = async () => {
    setBybitAnalyticsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/analytics/user-bybit`);
      setUserBybitAnalytics(response.data);
      setShowBybitAnalytics(true);
    } catch (error) {
      toast.error("Ошибка загрузки аналитики");
    } finally {
      setBybitAnalyticsLoading(false);
    }
  };

  const updateCommission = async () => {
    const value = parseFloat(newCommission);
    if (isNaN(value) || value < 0 || value > 50) {
      toast.error("Комиссия должна быть от 0 до 50%");
      return;
    }
    try {
      await axios.put(`${API}/admin/settings/commission?commission_percent=${value}`);
      setCommissionPercent(value);
      toast.success(`Комиссия обновлена: ${value}%`);
    } catch (error) {
      console.error("Error updating commission:", error);
      toast.error("Ошибка обновления комиссии");
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchCurrencies();
    fetchRequests();
    fetchCommission();
    fetchUsers();
    fetchSameCurrencyExchangeSetting();
    fetchUsdtCommission();
    fetchOperators();
    fetchChats();
    
    // WebSocket for admin notifications
    let ws = null;
    let reconnectTimeout = null;
    
    const connectAdminWS = () => {
      const wsUrl = API.replace('/api', '').replace('http', 'ws').replace('https', 'wss');
      ws = new WebSocket(`${wsUrl}/ws/admin`);
      
      ws.onopen = () => {
        console.log("Admin WebSocket connected");
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log("Admin WS message:", data);
          
          if (data.type === "new_request") {
            // Play notification sound
            playNotificationSound();
            toast.success(
              `🔔 Новая заявка!\n${data.from_amount} ${data.from_currency.toUpperCase()} → ${data.to_amount} ${data.to_currency.toUpperCase()}`,
              { duration: 10000 }
            );
            fetchRequests();
          }
          
          if (data.type === "deposit_confirmed") {
            playNotificationSound();
            toast.success(`💰 Депозит подтверждён!\n${data.amount} ${data.coin}\nЗаявка: ${data.request_id.slice(0,8)}...`);
            fetchRequests();
          }
        } catch (e) {
          console.error("WS parse error:", e);
        }
      };
      
      ws.onclose = () => {
        console.log("Admin WS closed, reconnecting...");
        reconnectTimeout = setTimeout(connectAdminWS, 5000);
      };
    };
    
    connectAdminWS();
    
    return () => {
      if (ws) ws.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
  }, []);

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API}/currencies`);
      setCurrencies(response.data);
      const initialRates = {};
      response.data.forEach((c) => {
        initialRates[c.coin_id] = {
          custom_rate: c.custom_rate || c.rate_usd,
          use_custom_rate: c.use_custom_rate
        };
      });
      setEditingRates(initialRates);
    } catch (error) {
      console.error("Error fetching currencies:", error);
      toast.error("Ошибка загрузки валют");
    }
  };

  const fetchRequests = async () => {
    try {
      // Fetch ALL requests including pending (not confirmed yet)
      const response = await axios.get(`${API}/exchange-requests?show_all=true`);
      setRequests(response.data);
    } catch (error) {
      console.error("Error fetching requests:", error);
      toast.error("Ошибка загрузки заявок");
    }
  };

  const handleSyncRates = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/sync-rates`);
      await fetchCurrencies();
      toast.success("Курсы обновлены");
    } catch (error) {
      console.error("Error syncing rates:", error);
      toast.error("Ошибка синхронизации");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRate = async (coinId) => {
    try {
      await axios.put(`${API}/currencies/${coinId}`, editingRates[coinId]);
      await fetchCurrencies();
      toast.success("Курс обновлен");
    } catch (error) {
      console.error("Error updating rate:", error);
      toast.error("Ошибка обновления курса");
    }
  };

  const handleUpdateRequestStatus = async (requestId, status) => {
    try {
      await axios.patch(`${API}/exchange-requests/${requestId}`, { status });
      await fetchRequests();
      toast.success("Статус обновлен");
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Ошибка обновления статуса");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_username");
    navigate("/admin/login");
  };

  const deleteRequest = async (requestId) => {
    if (!window.confirm("Удалить заявку? Это действие нельзя отменить.")) return;
    try {
      await axios.delete(`${API}/admin/exchange-requests/${requestId}`);
      toast.success("Заявка удалена");
      fetchRequests();
    } catch (error) {
      console.error("Error deleting request:", error);
      toast.error("Ошибка удаления заявки");
    }
  };

  const checkBalance = async (requestId) => {
    try {
      const response = await axios.get(`${API}/admin/exchange-requests/${requestId}/check-balance`);
      setBalances(prev => ({
        ...prev,
        [requestId]: response.data
      }));
      toast.success(`Баланс: ${response.data.balance} ${response.data.currency}`);
    } catch (error) {
      console.error("Error checking balance:", error);
      toast.error("Ошибка проверки баланса");
    }
  };

  const collectToHotWallet = async (requestId) => {
    if (!window.confirm("Собрать средства на горячий кошелек?")) return;
    
    try {
      const response = await axios.post(`${API}/admin/exchange-requests/${requestId}/collect-to-hot-wallet`);
      if (response.data.success) {
        toast.success(`Средства собраны! TX: ${response.data.tx_hash?.substring(0, 10)}...`);
        await fetchRequests();
      } else {
        toast.error(response.data.error || "Ошибка сбора средств");
      }
    } catch (error) {
      console.error("Error collecting funds:", error);
      toast.error("Ошибка сбора средств");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "text-yellow-500";
      case "awaiting_confirmation":
        return "text-secondary";
      case "deposit_confirmed":
        return "text-green-400";
      case "in_progress":
        return "text-blue-500";
      case "completed":
        return "text-green-500";
      case "cancelled":
        return "text-red-500";
      case "processing":
        return "text-blue-500";
      default:
        return "text-muted-foreground";
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      "pending": "Ожидание",
      "awaiting_confirmation": "Ожидание подтверждения",
      "deposit_confirmed": "✅ Депозит подтверждён",
      "in_progress": "📋 В работе",
      "processing": "Обработка",
      "completed": "✅ Завершено",
      "cancelled": "Отменено"
    };
    return statusMap[status] || status;
  };

  const fetchBybitBalance = async () => {
    setBybitLoading(true);
    try {
      const response = await axios.get(`${API}/admin/bybit/balance`);
      console.log("Bybit balance response:", response.data);
      if (response.data.success) {
        console.log("Setting balances:", response.data.balances);
        setBybitBalance(response.data.balances);
        toast.success("Баланс Bybit получен");
      } else {
        toast.error(response.data.error || "Ошибка получения баланса");
      }
    } catch (error) {
      console.error("Error fetching Bybit balance:", error);
      toast.error("Ошибка подключения к Bybit");
    } finally {
      setBybitLoading(false);
    }
  };

  const fetchBybitPrices = async () => {
    try {
      const response = await axios.get(`${API}/admin/bybit/prices`);
      if (response.data.success) {
        setBybitPrices(response.data.prices);
      }
    } catch (error) {
      console.error("Error fetching Bybit prices:", error);
    }
  };

  const executeBybitTrade = async (requestId) => {
    if (!window.confirm("Выполнить обмен через Bybit?")) return;
    
    try {
      const response = await axios.post(`${API}/admin/bybit/execute-trade`, {
        request_id: requestId
      });
      
      if (response.data.success) {
        toast.success(`Ордер отправлен! ID: ${response.data.order_id}`);
        await fetchRequests();
      } else {
        toast.error(response.data.error || "Ошибка выполнения обмена");
      }
    } catch (error) {
      console.error("Error executing Bybit trade:", error);
      toast.error("Ошибка выполнения обмена через Bybit");
    }
  };

  const executeAutoExchange = async (requestId) => {
    if (!window.confirm("Выполнить полный авто-обмен?\n\n1. Перевод из FUND в UNIFIED\n2. Торговля на Bybit\n3. Перевод обратно в FUND\n4. Вывод на кошелёк пользователя")) return;
    
    try {
      toast.info("Выполняется авто-обмен...");
      const response = await axios.post(`${API}/admin/bybit/auto-exchange?request_id=${requestId}`);
      
      if (response.data.success) {
        toast.success("Авто-обмен завершён успешно!");
        console.log("Exchange steps:", response.data.steps);
        await fetchRequests();
      } else {
        toast.error(response.data.error || "Ошибка авто-обмена");
        console.error("Exchange failed:", response.data);
      }
    } catch (error) {
      console.error("Error in auto exchange:", error);
      toast.error("Ошибка выполнения авто-обмена");
    }
  };

  const fetchBybitAddresses = async () => {
    try {
      const response = await axios.get(`${API}/admin/bybit/deposit-addresses`);
      if (response.data.success) {
        setBybitAddresses(response.data.addresses);
        toast.success(`Загружено ${response.data.count} адресов`);
      }
    } catch (error) {
      console.error("Error fetching Bybit addresses:", error);
      toast.error("Ошибка загрузки адресов");
    }
  };

  const syncBybitAddresses = async () => {
    setBybitLoading(true);
    try {
      const response = await axios.post(`${API}/admin/bybit/sync-addresses`);
      if (response.data.success) {
        toast.success(response.data.message);
        await fetchBybitAddresses();
      }
    } catch (error) {
      console.error("Error syncing Bybit addresses:", error);
      toast.error("Ошибка синхронизации адресов");
    } finally {
      setBybitLoading(false);
    }
  };

  const runDepositCheck = async () => {
    try {
      toast.info("Проверяем депозиты...");
      const response = await axios.post(`${API}/admin/deposit-checker/run-now`);
      if (response.data.success) {
        toast.success("Проверка депозитов завершена");
        await fetchRequests();
      }
    } catch (error) {
      console.error("Error running deposit check:", error);
      toast.error("Ошибка проверки депозитов");
    }
  };

  useEffect(() => {
    fetchAutoExchangeSetting();
    fetchExchangeProvider();
    fetchBybitKeys();
    fetchBybitKeysUsdt();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Hidden audio element for notifications */}
      <audio ref={audioRef} src={NOTIFICATION_SOUND} preload="auto" />
      
      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border p-6 rounded-lg w-full max-w-md">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Key size={20} />
              Смена пароля
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground">Текущий пароль</label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Введите текущий пароль"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Новый пароль</label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Минимум 6 символов"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Подтвердите пароль</label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Повторите новый пароль"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleChangePassword}
                  className="flex-1 bg-primary text-primary-foreground"
                >
                  Сохранить
                </Button>
                <Button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Отмена
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 py-6 flex justify-between items-center">
          <Link to="/" className="font-heading text-2xl font-bold text-primary tracking-tight hover:text-primary/80 transition-colors" data-testid="admin-title">
            SKINKOSH ADMIN
          </Link>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate("/admin/analytics")}
              variant="outline"
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
              data-testid="analytics-button"
            >
              📊 Аналитика
            </Button>
            <Button
              onClick={() => setShowPasswordModal(true)}
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground"
              title="Сменить пароль"
            >
              <Key size={16} />
            </Button>
            <span className="text-sm text-muted-foreground">
              {localStorage.getItem("admin_username")}
            </span>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-border hover:border-destructive hover:text-destructive"
              data-testid="logout-button"
            >
              <LogOut size={16} className="mr-2" />
              Выйти
            </Button>
          </div>
        </div>
      </header>

      {/* Dashboard Content */}
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-4xl grid-cols-6 bg-card border border-border mb-8">
            <TabsTrigger
              value="rates"
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              data-testid="rates-tab"
            >
              Курсы
            </TabsTrigger>
            <TabsTrigger
              value="requests"
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              data-testid="requests-tab"
            >
              Заявки
            </TabsTrigger>
            <TabsTrigger
              value="users"
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              data-testid="users-tab"
            >
              Юзеры
            </TabsTrigger>
            <TabsTrigger
              value="operators"
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              data-testid="operators-tab"
            >
              Операторы
            </TabsTrigger>
            <TabsTrigger
              value="chats"
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground relative"
              data-testid="chats-tab"
            >
              <MessageCircle size={14} className="mr-1" />
              Чаты
              {chats.filter(c => c.status === 'active').length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {chats.filter(c => c.status === 'active').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="bybit"
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              data-testid="bybit-tab"
            >
              Bybit
            </TabsTrigger>
          </TabsList>

          {/* Rates Tab */}
          <TabsContent value="rates" className="space-y-6">
            {/* Commission Settings */}
            <div className="bg-card border border-primary p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-bold text-primary">💰 Комиссия на обмены</h3>
                  <p className="text-sm text-muted-foreground">Текущая комиссия: <span className="font-bold text-primary">{commissionPercent}%</span></p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={newCommission}
                    onChange={(e) => setNewCommission(e.target.value)}
                    className="w-24 h-10 font-mono text-center"
                    min="0"
                    max="50"
                    step="0.1"
                    placeholder="%"
                  />
                  <span className="text-muted-foreground">%</span>
                  <Button
                    onClick={updateCommission}
                    size="sm"
                    className="bg-primary text-primary-foreground"
                  >
                    Сохранить
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <h2 className="font-heading text-xl font-bold text-foreground uppercase">
                Курсы криптовалют
              </h2>
              <Button
                onClick={handleSyncRates}
                disabled={loading}
                className="bg-secondary text-secondary-foreground hover:translate-x-1 hover:translate-y-1 transition-transform brutal-shadow"
                data-testid="sync-rates-button"
              >
                <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
                Синхронизировать
              </Button>
            </div>

            <div className="grid gap-4">
              {currencies.map((currency) => (
                <div
                  key={currency.coin_id}
                  className="bg-card border border-border p-4 hover:border-primary/50 transition-colors"
                  data-testid={`currency-card-${currency.coin_id}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 flex-1">
                      <span className="text-2xl">{currency.icon}</span>
                      <div>
                        <div className="font-bold font-mono">{currency.symbol}</div>
                        <div className="text-xs text-muted-foreground">{currency.name}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 flex-1">
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Bybit курс</div>
                        <div className="font-mono font-bold">${currency.rate_usd.toFixed(4)}</div>
                      </div>
                      
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">С наценкой +{commissionPercent}%</div>
                        <div className="font-mono font-bold text-green-500">
                          ${(currency.rate_usd * (1 + commissionPercent / 100)).toFixed(4)}
                        </div>
                      </div>

                      <div className="flex-1">
                        <div className="text-xs text-muted-foreground mb-1">Кастомный курс</div>
                        <Input
                          type="number"
                          step="0.0001"
                          value={editingRates[currency.coin_id]?.custom_rate || ""}
                          onChange={(e) =>
                            setEditingRates({
                              ...editingRates,
                              [currency.coin_id]: {
                                ...editingRates[currency.coin_id],
                                custom_rate: parseFloat(e.target.value) || 0
                              }
                            })
                          }
                          className="h-10 bg-input border-transparent focus:border-primary font-mono"
                          data-testid={`custom-rate-input-${currency.coin_id}`}
                        />
                      </div>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingRates[currency.coin_id]?.use_custom_rate || false}
                          onChange={(e) =>
                            setEditingRates({
                              ...editingRates,
                              [currency.coin_id]: {
                                ...editingRates[currency.coin_id],
                                use_custom_rate: e.target.checked
                              }
                            })
                          }
                          className="w-4 h-4"
                          data-testid={`use-custom-rate-checkbox-${currency.coin_id}`}
                        />
                        <span className="text-xs text-muted-foreground">Использовать</span>
                      </label>

                      <Button
                        onClick={() => handleUpdateRate(currency.coin_id)}
                        size="sm"
                        className="bg-primary text-primary-foreground"
                        data-testid={`save-rate-button-${currency.coin_id}`}
                      >
                        Сохранить
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Requests Tab */}
          <TabsContent value="requests" className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex items-center gap-4">
                <h2 className="font-heading text-xl font-bold text-foreground uppercase">
                  Заявки на обмен ({filteredRequests.length}{searchWallet && ` из ${requests.length}`})
                </h2>
                <label className="flex items-center gap-2 cursor-pointer">
                  <div 
                    onClick={toggleAutoExchange}
                    className={`relative w-12 h-6 rounded-full transition-colors ${autoExchangeEnabled ? 'bg-green-500' : 'bg-gray-600'}`}
                  >
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${autoExchangeEnabled ? 'translate-x-7' : 'translate-x-1'}`}></div>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    Автообмен {autoExchangeEnabled ? '✅' : '❌'}
                  </span>
                </label>
                
                {/* Exchange Provider Toggle */}
                <div className="flex items-center gap-2 ml-4 border-l border-border pl-4">
                  <span className="text-xs text-muted-foreground">Биржа:</span>
                  <div className="flex bg-card border border-border rounded overflow-hidden">
                    <button
                      onClick={() => switchExchangeProvider("bybit")}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${
                        exchangeProvider === "bybit" 
                          ? "bg-primary text-primary-foreground" 
                          : "hover:bg-muted"
                      }`}
                    >
                      Bybit
                    </button>
                    <button
                      onClick={() => switchExchangeProvider("whitebit")}
                      disabled={!whitebitConfigured}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${
                        exchangeProvider === "whitebit" 
                          ? "bg-green-500 text-white" 
                          : whitebitConfigured 
                            ? "hover:bg-muted" 
                            : "opacity-50 cursor-not-allowed"
                      }`}
                      title={!whitebitConfigured ? "WhiteBIT не настроен" : ""}
                    >
                      WhiteBIT
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 w-full md:w-auto">
                <Input
                  type="text"
                  value={searchWallet}
                  onChange={(e) => setSearchWallet(e.target.value)}
                  placeholder="🔍 Поиск по кошельку, email, номеру..."
                  className="h-10 bg-input border-border w-full md:w-80 font-mono text-sm"
                />
                {searchWallet && (
                  <Button
                    onClick={() => setSearchWallet("")}
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    ✕
                  </Button>
                )}
              </div>
            </div>

            {/* Status Filter Tabs */}
            <div className="flex flex-wrap gap-2 mb-4">
              {STATUS_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setStatusFilter(filter.value)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                    statusFilter === filter.value
                      ? `${filter.color} text-white shadow-lg`
                      : "bg-card border border-border text-muted-foreground hover:text-foreground hover:border-primary/50"
                  }`}
                >
                  {filter.label} ({statusCounts[filter.value] || 0})
                </button>
              ))}
            </div>

            {filteredRequests.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                {searchWallet ? "Заявки не найдены" : "Нет заявок"}
              </div>
            ) : (
              <>
              <div className="grid gap-4">
                {paginatedRequests.map((request, index) => (
                  <div
                    key={request.id}
                    className="bg-card border border-border p-4 hover:border-primary/50 transition-colors"
                    data-testid={`request-card-${request.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-3">
                          <span 
                            className="bg-primary text-primary-foreground font-bold text-sm px-2 py-1 rounded font-mono cursor-pointer hover:bg-primary/80 transition-colors"
                            onClick={() => copyToClipboard(request.order_number || String(filteredRequests.length - ((requestsPage - 1) * REQUESTS_PER_PAGE + index)), "Номер заявки скопирован")}
                            title="Нажмите чтобы скопировать"
                          >
                            #{request.order_number || filteredRequests.length - ((requestsPage - 1) * REQUESTS_PER_PAGE + index)}
                          </span>
                          <span className={`font-mono font-bold text-sm uppercase ${getStatusColor(request.status)}`}>
                            {getStatusText(request.status)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(request.created_at).toLocaleString('ru-RU')}
                          </span>
                          {request.deposit_txid && (
                            <span className="text-xs text-green-500">
                              TX: {request.deposit_txid.slice(0,10)}...
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div>
                            <div className="text-xs text-muted-foreground mb-1">Отправляет</div>
                            <div 
                              className="font-mono font-bold cursor-pointer hover:text-primary transition-colors"
                              onClick={() => copyToClipboard(`${request.from_amount}`, "Сумма скопирована")}
                              title="Нажмите чтобы скопировать"
                            >
                              {request.from_amount} {request.from_currency.toUpperCase()}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground mb-1">Получает</div>
                            <div 
                              className="font-mono font-bold text-primary cursor-pointer hover:text-primary/80 transition-colors"
                              onClick={() => copyToClipboard(`${request.to_amount}`, "Сумма скопирована")}
                              title="Нажмите чтобы скопировать"
                            >
                              {request.to_amount} {request.to_currency.toUpperCase()}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-1">
                          {/* Hidden contacts toggle */}
                          <div className="text-xs">
                            <button
                              onClick={() => toggleContactVisibility(request.id)}
                              className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
                            >
                              {visibleContacts[request.id] ? "👁 Скрыть контакты" : "👁‍🗨 Показать контакты"}
                            </button>
                          </div>
                          
                          {visibleContacts[request.id] && (
                            <>
                              <div className="text-xs">
                                <span className="text-muted-foreground">Email:</span>{" "}
                                <span 
                                  className="font-mono cursor-pointer hover:text-primary transition-colors"
                                  onClick={() => copyToClipboard(request.user_email, "Email скопирован")}
                                  title="Нажмите чтобы скопировать"
                                >
                                  {request.user_email}
                                </span>
                              </div>
                              {request.user_telegram && (
                                <div className="text-xs">
                                  <span className="text-muted-foreground">Telegram:</span>{" "}
                                  <span
                                    className="font-mono cursor-pointer hover:text-primary transition-colors"
                                    onClick={() => copyToClipboard(request.user_telegram, "Telegram скопирован")}
                                    title="Нажмите чтобы скопировать"
                                  >
                                    {request.user_telegram}
                                  </span>
                                  {" "}
                                  <a 
                                    href={`https://t.me/${request.user_telegram.replace('@', '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-400 hover:text-blue-300"
                                  >
                                    ↗
                                  </a>
                                </div>
                              )}
                            </>
                          )}
                          
                          <div className="text-xs">
                            <span className="text-muted-foreground">Адрес для оплаты:</span>{" "}
                            <span className="font-mono text-xs break-all text-muted-foreground">
                              {request.deposit_address}
                            </span>
                          </div>
                          <div className="text-xs">
                            <span className="text-muted-foreground">Кошелек клиента:</span>{" "}
                            <span 
                              className="font-mono text-xs break-all cursor-pointer hover:text-primary text-primary transition-colors"
                              onClick={() => copyToClipboard(request.user_wallet, "Кошелек скопирован")}
                              title="Нажмите чтобы скопировать"
                            >
                              {request.user_wallet}
                            </span>
                          </div>
                          {balances[request.id] && (
                            <div className="mt-3 p-3 bg-primary/10 border border-primary">
                              <div className="text-xs font-bold text-primary mb-1">БАЛАНС ДЕПОЗИТА</div>
                              <div className="font-mono text-lg font-bold">
                                {balances[request.id].balance} {balances[request.id].currency}
                              </div>
                              {balances[request.id].transactions && balances[request.id].transactions.length > 0 && (
                                <div className="text-xs text-muted-foreground mt-2">
                                  Транзакций: {balances[request.id].transactions.length}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={() => checkBalance(request.id)}
                          size="sm"
                          variant="outline"
                          className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                          data-testid={`check-balance-${request.id}`}
                        >
                          <Wallet size={14} className="mr-1" />
                          Проверить баланс
                        </Button>
                        
                        {balances[request.id] && balances[request.id].balance > 0 && (
                          <Button
                            onClick={() => collectToHotWallet(request.id)}
                            size="sm"
                            className="bg-secondary text-secondary-foreground"
                            data-testid={`collect-funds-${request.id}`}
                          >
                            <ArrowDownToLine size={14} className="mr-1" />
                            Собрать средства
                          </Button>
                        )}
                        
                        {(request.status === "pending" || request.status === "awaiting_confirmation") && (
                          <>
                            <Button
                              onClick={() => handleUpdateRequestStatus(request.id, "in_progress")}
                              size="sm"
                              className="bg-blue-600 text-white hover:bg-blue-700"
                              data-testid={`in-progress-${request.id}`}
                            >
                              📋 В работу
                            </Button>
                            <Button
                              onClick={() => executeAutoExchange(request.id)}
                              size="sm"
                              className="bg-green-600 text-white hover:bg-green-700"
                              data-testid={`auto-exchange-${request.id}`}
                            >
                              <ArrowRightLeft size={14} className="mr-1" />
                              🚀 Авто-обмен
                            </Button>
                            <Button
                              onClick={() => executeBybitTrade(request.id)}
                              size="sm"
                              variant="outline"
                              className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white"
                              data-testid={`bybit-trade-${request.id}`}
                            >
                              <ArrowRightLeft size={14} className="mr-1" />
                              Только торговля
                            </Button>
                            <Button
                              onClick={() => handleUpdateRequestStatus(request.id, "completed")}
                              size="sm"
                              variant="outline"
                              className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
                              data-testid={`complete-button-${request.id}`}
                            >
                              Завершить
                            </Button>
                            <Button
                              onClick={() => handleUpdateRequestStatus(request.id, "cancelled")}
                              size="sm"
                              variant="outline"
                              className="border-destructive text-destructive hover:bg-destructive hover:text-white"
                              data-testid={`cancel-button-${request.id}`}
                            >
                              Отменить
                            </Button>
                          </>
                        )}
                        {request.status === "deposit_confirmed" && (
                          <div className="flex flex-col gap-2">
                            <Button
                              onClick={() => handleUpdateRequestStatus(request.id, "in_progress")}
                              size="sm"
                              className="bg-blue-600 text-white hover:bg-blue-700"
                            >
                              📋 Взял в работу
                            </Button>
                            <Button
                              onClick={() => handleUpdateRequestStatus(request.id, "completed")}
                              size="sm"
                              className="bg-green-600 text-white hover:bg-green-700"
                            >
                              ✅ Завершить обмен
                            </Button>
                          </div>
                        )}
                        {request.status === "in_progress" && (
                          <div className="flex flex-col gap-2">
                            <div className="text-xs text-blue-500 font-mono mb-1">
                              📋 В работе
                            </div>
                            <Button
                              onClick={() => handleUpdateRequestStatus(request.id, "completed")}
                              size="sm"
                              className="bg-green-600 text-white hover:bg-green-700"
                            >
                              ✅ Завершить обмен
                            </Button>
                          </div>
                        )}
                        {request.status === "processing" && (
                          <div className="flex flex-col gap-2">
                            <Button
                              onClick={() => handleUpdateRequestStatus(request.id, "completed")}
                              size="sm"
                              className="bg-green-600 text-white hover:bg-green-700"
                            >
                              ✅ Завершить обмен
                            </Button>
                          </div>
                        )}
                        
                        {/* Delete button - always visible */}
                        <Button
                          onClick={() => deleteRequest(request.id)}
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:bg-red-500/10 hover:text-red-600 mt-2"
                          data-testid={`delete-button-${request.id}`}
                        >
                          <Trash2 size={14} className="mr-1" />
                          Удалить
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination */}
              {totalRequestsPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-6 pt-4 border-t border-border">
                  <Button
                    onClick={() => setRequestsPage(p => Math.max(1, p - 1))}
                    variant="outline"
                    size="sm"
                    disabled={requestsPage === 1}
                    className="border-border"
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  
                  <div className="flex items-center gap-2">
                    {/* First page */}
                    {requestsPage > 2 && (
                      <>
                        <Button
                          onClick={() => setRequestsPage(1)}
                          variant={requestsPage === 1 ? "default" : "ghost"}
                          size="sm"
                          className="w-8 h-8 p-0"
                        >
                          1
                        </Button>
                        {requestsPage > 3 && <span className="text-muted-foreground">...</span>}
                      </>
                    )}
                    
                    {/* Pages around current */}
                    {Array.from({ length: Math.min(5, totalRequestsPages) }, (_, i) => {
                      let pageNum;
                      if (totalRequestsPages <= 5) {
                        pageNum = i + 1;
                      } else if (requestsPage <= 3) {
                        pageNum = i + 1;
                      } else if (requestsPage >= totalRequestsPages - 2) {
                        pageNum = totalRequestsPages - 4 + i;
                      } else {
                        pageNum = requestsPage - 2 + i;
                      }
                      
                      if (pageNum < 1 || pageNum > totalRequestsPages) return null;
                      if (pageNum === 1 && requestsPage > 2) return null;
                      if (pageNum === totalRequestsPages && requestsPage < totalRequestsPages - 1) return null;
                      
                      return (
                        <Button
                          key={pageNum}
                          onClick={() => setRequestsPage(pageNum)}
                          variant={requestsPage === pageNum ? "default" : "ghost"}
                          size="sm"
                          className={`w-8 h-8 p-0 ${requestsPage === pageNum ? 'bg-primary text-primary-foreground' : ''}`}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    
                    {/* Last page */}
                    {requestsPage < totalRequestsPages - 1 && (
                      <>
                        {requestsPage < totalRequestsPages - 2 && <span className="text-muted-foreground">...</span>}
                        <Button
                          onClick={() => setRequestsPage(totalRequestsPages)}
                          variant={requestsPage === totalRequestsPages ? "default" : "ghost"}
                          size="sm"
                          className="w-8 h-8 p-0"
                        >
                          {totalRequestsPages}
                        </Button>
                      </>
                    )}
                  </div>
                  
                  <Button
                    onClick={() => setRequestsPage(p => Math.min(totalRequestsPages, p + 1))}
                    variant="outline"
                    size="sm"
                    disabled={requestsPage === totalRequestsPages}
                    className="border-border"
                  >
                    <ChevronRight size={16} />
                  </Button>
                  
                  <span className="text-sm text-muted-foreground ml-4">
                    Страница {requestsPage} из {totalRequestsPages} ({filteredRequests.length} заявок)
                  </span>
                </div>
              )}
              </>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            {/* Same Currency Exchange Setting */}
            <div className="bg-card border border-primary p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-bold text-primary">⚙️ Обмен одинаковых валют (USDT→USDT)</h3>
                  <p className="text-sm text-muted-foreground">
                    Разрешить обмен между одинаковыми валютами разных сетей (например USDT TRC20 → USDT ERC20)
                  </p>
                </div>
                <Button
                  onClick={toggleSameCurrencyExchange}
                  variant={allowSameCurrencyExchange ? "default" : "outline"}
                  className={allowSameCurrencyExchange ? "bg-green-500 hover:bg-green-600" : ""}
                >
                  {allowSameCurrencyExchange ? "✓ Разрешено" : "✗ Запрещено"}
                </Button>
              </div>
            </div>

            {/* USDT/USDT Fixed Fee */}
            <div className="bg-card border border-green-500/50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-bold text-green-500">💰 Фикс комиссия USDT/USDT</h3>
                  <p className="text-sm text-muted-foreground">
                    Текущая комиссия: <span className="font-bold text-green-500">${usdtFee}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={editUsdtFee}
                    onChange={(e) => setEditUsdtFee(e.target.value)}
                    placeholder={`$${usdtFee}`}
                    className="w-24 bg-input border-border"
                  />
                  <Button
                    onClick={updateUsdtCommission}
                    size="sm"
                    className="bg-green-500 hover:bg-green-600"
                  >
                    Сохранить
                  </Button>
                </div>
              </div>
            </div>

            {/* USDT/USDT Bybit API Keys */}
            <div className="bg-card border border-orange-500/50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading font-bold text-orange-500">🔑 Bybit API для USDT/USDT</h3>
                  <p className="text-sm text-muted-foreground">
                    Отдельные API ключи Bybit для обмена USDT на USDT (разные сети)
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-bold ${bybitKeysUsdtConfigured ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                    {bybitKeysUsdtConfigured ? '✓ Настроено' : '✗ Не настроено'}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowBybitKeysUsdt(!showBybitKeysUsdt)}
                    className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                  >
                    <Key size={14} className="mr-1" />
                    {showBybitKeysUsdt ? 'Скрыть' : 'Настроить'}
                  </Button>
                </div>
              </div>
              
              {showBybitKeysUsdt && (
                <div className="space-y-3 pt-3 border-t border-orange-500/20">
                  <div>
                    <label className="text-xs text-muted-foreground">API Key</label>
                    <Input
                      type="text"
                      value={bybitApiKeyUsdt}
                      onChange={(e) => setBybitApiKeyUsdt(e.target.value)}
                      placeholder="Введите API ключ Bybit для USDT/USDT"
                      className="bg-input border-border"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">API Secret</label>
                    <Input
                      type="password"
                      value={bybitApiSecretUsdt}
                      onChange={(e) => setBybitApiSecretUsdt(e.target.value)}
                      placeholder="Введите API секрет Bybit для USDT/USDT"
                      className="bg-input border-border"
                    />
                  </div>
                  <Button
                    onClick={updateBybitKeysUsdt}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    Сохранить ключи USDT/USDT
                  </Button>
                </div>
              )}
            </div>

            {/* Users List */}
            <div className="bg-card border border-border rounded-lg">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h2 className="font-heading text-xl font-bold text-primary">👥 Пользователи ({users.length})</h2>
                <div className="flex gap-2">
                  <Button onClick={fetchUserBybitAnalytics} variant="outline" size="sm" disabled={bybitAnalyticsLoading} className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                    📊 Аналитика Bybit
                  </Button>
                  <Button onClick={fetchUsers} variant="outline" size="sm" disabled={usersLoading}>
                    <RefreshCw size={16} className={usersLoading ? "animate-spin mr-2" : "mr-2"} />
                    Обновить
                  </Button>
                </div>
              </div>
              
              {usersLoading ? (
                <div className="p-8 text-center text-muted-foreground">Загрузка...</div>
              ) : users.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">Нет зарегистрированных пользователей</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left p-3 text-muted-foreground font-medium">Email</th>
                        <th className="text-left p-3 text-muted-foreground font-medium">Telegram</th>
                        <th className="text-center p-3 text-muted-foreground font-medium">KYC</th>
                        <th className="text-center p-3 text-muted-foreground font-medium">USDT/USDT</th>
                        <th className="text-center p-3 text-muted-foreground font-medium">Bybit API</th>
                        <th className="text-center p-3 text-muted-foreground font-medium">Обменов</th>
                        <th className="text-center p-3 text-muted-foreground font-medium">Объём</th>
                        <th className="text-left p-3 text-muted-foreground font-medium">Дата</th>
                        <th className="text-center p-3 text-muted-foreground font-medium">Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id} className="border-b border-border hover:bg-muted/20">
                          <td className="p-3">
                            <span className="font-mono text-sm">{user.email}</span>
                          </td>
                          <td className="p-3">
                            <span className="text-sm">{user.telegram || "—"}</span>
                          </td>
                          <td className="p-3 text-center">
                            {user.kyc_verified ? (
                              <span className="text-green-500 font-bold">✓</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            <Button
                              onClick={() => toggleUserSameCurrency(user.id, user.allow_same_currency_exchange)}
                              variant="outline"
                              size="sm"
                              className={user.allow_same_currency_exchange 
                                ? "bg-green-500/20 text-green-500 hover:bg-green-500/30 border-green-500/50" 
                                : "text-muted-foreground hover:text-foreground"}
                              title={user.allow_same_currency_exchange ? "Запретить USDT/USDT" : "Разрешить USDT/USDT"}
                            >
                              {user.allow_same_currency_exchange ? "✓" : "✗"}
                            </Button>
                          </td>
                          <td className="p-3 text-center">
                            <Button
                              onClick={() => openUserBybitModal(user)}
                              variant="outline"
                              size="sm"
                              className={user.bybit_api_key 
                                ? "bg-orange-500/20 text-orange-500 hover:bg-orange-500/30 border-orange-500/50" 
                                : "text-muted-foreground hover:text-foreground"}
                              title={user.bybit_api_key ? "Bybit API настроен" : "Настроить Bybit API"}
                            >
                              <Key size={14} />
                            </Button>
                          </td>
                          <td className="p-3 text-center">
                            <span className="font-bold">{user.exchange_count || 0}</span>
                          </td>
                          <td className="p-3 text-center">
                            <span className="text-primary font-bold">
                              ${(user.total_volume || 0).toFixed(2)}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className="text-xs text-muted-foreground">
                              {user.created_at ? new Date(user.created_at).toLocaleDateString('ru-RU') : "—"}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2 justify-center">
                              <Button
                                onClick={() => updateUserKyc(user.id, !user.kyc_verified)}
                                variant="outline"
                                size="sm"
                                className={user.kyc_verified ? "text-red-500 hover:text-red-400" : "text-green-500 hover:text-green-400"}
                                title={user.kyc_verified ? "Отменить верификацию" : "Верифицировать"}
                              >
                                {user.kyc_verified ? "✗ KYC" : "✓ KYC"}
                              </Button>
                              <Button
                                onClick={() => deleteUser(user.id)}
                                variant="outline"
                                size="sm"
                                className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                title="Удалить пользователя"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Operators Tab */}
          <TabsContent value="operators" className="space-y-6">
            <div className="bg-card border border-border p-6 rounded-lg">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-primary">Управление операторами</h3>
                <Button
                  onClick={() => setShowAddOperator(!showAddOperator)}
                  className="bg-primary text-primary-foreground"
                >
                  <UserPlus size={18} className="mr-2" />
                  Добавить оператора
                </Button>
              </div>

              {/* Add Operator Form */}
              {showAddOperator && (
                <div className="mb-6 p-4 bg-muted/30 rounded-lg border border-border">
                  <h4 className="font-bold mb-4">Новый оператор</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      placeholder="Логин оператора"
                      value={newOperatorUsername}
                      onChange={(e) => setNewOperatorUsername(e.target.value)}
                      className="bg-input"
                    />
                    <Input
                      type="password"
                      placeholder="Пароль"
                      value={newOperatorPassword}
                      onChange={(e) => setNewOperatorPassword(e.target.value)}
                      className="bg-input"
                    />
                    <div className="flex gap-2">
                      <Button onClick={createOperator} className="bg-green-600 hover:bg-green-700">
                        Создать
                      </Button>
                      <Button variant="outline" onClick={() => setShowAddOperator(false)}>
                        Отмена
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Operators List */}
              {operators.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Операторы не найдены</p>
                  <p className="text-sm mt-2">Нажмите "Добавить оператора" чтобы создать нового</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4">Логин</th>
                        <th className="text-left py-3 px-4">Роль</th>
                        <th className="text-left py-3 px-4">Заявок обработано</th>
                        <th className="text-left py-3 px-4">Создан</th>
                        <th className="text-right py-3 px-4">Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {operators.map((operator) => (
                        <tr key={operator.id} className="border-b border-border/50 hover:bg-muted/20">
                          <td className="py-3 px-4 font-mono">{operator.username}</td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-1 rounded bg-blue-500/20 text-blue-400 text-sm">
                              Оператор
                            </span>
                          </td>
                          <td className="py-3 px-4">{operator.requests_count || 0}</td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">
                            {new Date(operator.created_at).toLocaleDateString('ru-RU')}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => deleteOperator(operator.id)}
                              className="text-red-500 border-red-500/50 hover:bg-red-500/10"
                            >
                              <Trash2 size={14} className="mr-1" />
                              Удалить
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Info */}
              <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-sm text-blue-400">
                  💡 Операторы могут только просматривать и обрабатывать заявки. 
                  У них нет доступа к настройкам, курсам и другим функциям администратора.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Chats Tab */}
          <TabsContent value="chats" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-heading text-xl font-bold">
                <MessageCircle className="inline mr-2" size={24} />
                Онлайн чаты
              </h2>
              <Button onClick={fetchChats} variant="outline" disabled={chatsLoading}>
                <RefreshCw size={16} className={chatsLoading ? "animate-spin" : ""} />
                Обновить
              </Button>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              {/* Chat List */}
              <div className="lg:col-span-1 space-y-2">
                <h3 className="font-semibold mb-3 text-muted-foreground">Активные чаты</h3>
                {chats.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Нет активных чатов</p>
                ) : (
                  chats.map((chat) => (
                    <div
                      key={chat.session_id}
                      onClick={() => setSelectedChat(chat)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedChat?.session_id === chat.session_id 
                          ? 'bg-primary/10 border-primary' 
                          : 'bg-card border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-semibold">{chat.user_name}</div>
                          <div className="text-xs text-muted-foreground">{chat.user_email || 'Email не указан'}</div>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs ${chat.status === 'active' ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}`}>
                          {chat.status === 'active' ? 'Активен' : 'Закрыт'}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-2">
                        {chat.messages?.length || 0} сообщений • {new Date(chat.updated_at).toLocaleString('ru-RU')}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Chat Window */}
              <div className="lg:col-span-2">
                {selectedChat ? (
                  <div className="bg-card border border-border rounded-lg overflow-hidden">
                    {/* Chat Header */}
                    <div className="bg-primary/10 px-4 py-3 flex justify-between items-center border-b border-border">
                      <div>
                        <div className="font-bold">{selectedChat.user_name}</div>
                        <div className="text-xs text-muted-foreground">{selectedChat.user_email}</div>
                      </div>
                      <div className="flex gap-2">
                        {selectedChat.status === 'active' && (
                          <Button onClick={() => closeChat(selectedChat.session_id)} variant="outline" size="sm" className="text-red-500 border-red-500">
                            Закрыть чат
                          </Button>
                        )}
                        <Button onClick={() => deleteChat(selectedChat.session_id)} variant="outline" size="sm" className="text-red-500 border-red-500 hover:bg-red-500 hover:text-white">
                          Удалить
                        </Button>
                      </div>
                    </div>

                    {/* Messages */}
                    <div className="h-[400px] overflow-y-auto p-4 space-y-3">
                      {selectedChat.messages?.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender === 'operator' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[70%] px-4 py-2 rounded-2xl ${
                              msg.sender === 'operator'
                                ? 'bg-primary text-primary-foreground rounded-br-md'
                                : 'bg-muted text-foreground rounded-bl-md'
                            }`}
                          >
                            <p className="text-sm">{msg.text}</p>
                            <p className="text-[10px] opacity-60 mt-1">
                              {new Date(msg.timestamp).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Reply Input */}
                    {selectedChat.status === 'active' && (
                      <div className="p-4 border-t border-border">
                        <div className="flex gap-2">
                          <Input
                            value={chatReply}
                            onChange={(e) => setChatReply(e.target.value)}
                            placeholder="Введите ответ..."
                            onKeyPress={(e) => e.key === 'Enter' && sendChatReply()}
                            className="flex-1"
                          />
                          <Button onClick={sendChatReply} disabled={!chatReply.trim()}>
                            <Send size={16} />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-card border border-border rounded-lg h-[500px] flex items-center justify-center text-muted-foreground">
                    Выберите чат для просмотра сообщений
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Bybit Tab */}
          <TabsContent value="bybit" className="space-y-6">
            {/* Exchange Provider Indicator */}
            <div className="flex items-center gap-4 p-4 bg-card border border-border rounded-lg">
              <span className="font-bold">Активная биржа:</span>
              <span className={`px-3 py-1 rounded font-bold ${exchangeProvider === 'bybit' ? 'bg-orange-500 text-white' : 'bg-green-500 text-white'}`}>
                {exchangeProvider === 'bybit' ? '🟠 Bybit' : '🟢 WhiteBIT'}
              </span>
              <span className="text-muted-foreground text-sm">
                (переключить можно на вкладке "Заявки")
              </span>
            </div>

            {/* Bybit Section */}
            <div className="border border-orange-500/30 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-heading text-xl font-bold text-orange-500 uppercase">
                  🟠 Bybit Integration
                </h2>
                <div className="flex gap-2">
                  <Button
                    onClick={runDepositCheck}
                    variant="outline"
                    className="border-green-500 text-green-500 hover:bg-green-500 hover:text-white"
                  >
                    🔄 Проверить депозиты
                  </Button>
                  <Button
                    onClick={fetchBybitPrices}
                    variant="outline"
                    className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                  >
                    <TrendingUp size={16} className="mr-2" />
                    Курсы
                  </Button>
                  <Button
                    onClick={fetchBybitBalance}
                    disabled={bybitLoading}
                    className="bg-orange-500 text-white hover:bg-orange-600"
                  >
                    <Wallet size={16} className={`mr-2 ${bybitLoading ? 'animate-spin' : ''}`} />
                    Баланс
                  </Button>
                </div>
              </div>

            {/* Bybit Account Balance */}
            {bybitBalance && (
              <div className="bg-card border border-border p-6">
                <h3 className="font-heading text-lg font-bold mb-4">💰 Баланс Bybit аккаунта</h3>
                {bybitBalance.length === 0 ? (
                  <div className="text-muted-foreground">Нет средств на аккаунте</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {bybitBalance.map((coin, idx) => (
                      <div key={`${coin.coin}-${coin.account_type || idx}`} className="bg-muted p-4 border border-border">
                        <div className="flex items-center justify-between mb-1">
                          <div className="font-mono font-bold text-primary">{coin.coin}</div>
                          <div className={`text-xs px-2 py-0.5 rounded ${coin.account_type === 'FUND' ? 'bg-orange-500/20 text-orange-500' : 'bg-blue-500/20 text-blue-500'}`}>
                            {coin.account_type === 'FUND' ? 'Фин.' : 'Торговый'}
                          </div>
                        </div>
                        <div className="font-mono text-lg">{coin.wallet_balance.toFixed(4)}</div>
                        {coin.usd_value > 0 && (
                          <div className="text-xs text-muted-foreground">
                            ≈ ${coin.usd_value.toFixed(2)}
                          </div>
                        )}
                        <div className="text-xs text-green-500 mt-1">
                          Доступно: {coin.available_balance.toFixed(4)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Bybit Market Prices */}
            {bybitPrices.length > 0 && (
              <div className="bg-card border border-border p-6">
                <h3 className="font-heading text-lg font-bold mb-4">📊 Рыночные цены Bybit</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {bybitPrices.map((price) => (
                    <div key={price.symbol} className="bg-muted p-4 border border-border">
                      <div className="font-mono font-bold">{price.symbol}</div>
                      <div className="font-mono text-lg text-primary">
                        ${price.last_price.toLocaleString()}
                      </div>
                      <div className={`text-xs ${price.change_24h >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {price.change_24h >= 0 ? '↑' : '↓'} {Math.abs(price.change_24h).toFixed(2)}%
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Vol: {(price.volume_24h / 1000000).toFixed(2)}M
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bybit Deposit Addresses */}
            <div className="bg-card border border-border p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-heading text-lg font-bold">📥 Депозитные адреса Bybit</h3>
                <div className="flex gap-2">
                  <Button
                    onClick={fetchBybitAddresses}
                    variant="outline"
                    size="sm"
                  >
                    Загрузить
                  </Button>
                  <Button
                    onClick={syncBybitAddresses}
                    disabled={bybitLoading}
                    size="sm"
                    className="bg-orange-500 text-white hover:bg-orange-600"
                  >
                    <RefreshCw size={14} className={`mr-1 ${bybitLoading ? 'animate-spin' : ''}`} />
                    Синхронизировать
                  </Button>
                </div>
              </div>
              
              {bybitAddresses.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {bybitAddresses.map((addr) => (
                    <div key={addr.coin_id} className="bg-muted p-3 border border-border flex items-center justify-between">
                      <div>
                        <span className="font-mono font-bold text-primary">{addr.symbol}</span>
                        <span className="text-xs text-muted-foreground ml-2">({addr.network})</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-background px-2 py-1 rounded font-mono">
                          {addr.address.slice(0, 20)}...{addr.address.slice(-8)}
                        </code>
                        {addr.tag && (
                          <span className="text-xs bg-yellow-500/20 text-yellow-500 px-2 py-1 rounded">
                            Tag: {addr.tag}
                          </span>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            navigator.clipboard.writeText(addr.address);
                            toast.success(`Адрес ${addr.symbol} скопирован`);
                          }}
                        >
                          <Copy size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-4">
                  Нажмите «Загрузить» для получения адресов
                </div>
              )}
            </div>

            {/* Bybit API Keys Settings */}
            <div className="bg-card border border-border p-6 rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-heading text-lg font-bold">🔑 API Ключи Bybit</h3>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs ${bybitKeysConfigured ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                    {bybitKeysConfigured ? '✓ Настроено' : '✗ Не настроено'}
                  </span>
                  <Button
                    onClick={() => setShowBybitKeys(!showBybitKeys)}
                    variant="outline"
                    size="sm"
                  >
                    {showBybitKeys ? 'Скрыть' : 'Изменить'}
                  </Button>
                </div>
              </div>
              
              {showBybitKeys && (
                <div className="space-y-4 pt-4 border-t border-border">
                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">API Key</label>
                    <input
                      type="text"
                      value={bybitApiKey}
                      onChange={(e) => setBybitApiKey(e.target.value)}
                      placeholder="Введите Bybit API Key"
                      className="w-full p-2 bg-background border border-border rounded font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">API Secret</label>
                    <input
                      type="password"
                      value={bybitApiSecret}
                      onChange={(e) => setBybitApiSecret(e.target.value)}
                      placeholder="Введите Bybit API Secret"
                      className="w-full p-2 bg-background border border-border rounded font-mono text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={updateBybitKeys}
                      className="bg-orange-500 text-white hover:bg-orange-600"
                    >
                      💾 Сохранить ключи
                    </Button>
                    <Button
                      onClick={() => {
                        setShowBybitKeys(false);
                        setBybitApiKey("");
                        setBybitApiSecret("");
                      }}
                      variant="outline"
                    >
                      Отмена
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Ключи будут проверены перед сохранением. Получить ключи можно в{' '}
                    <a href="https://www.bybit.com/app/user/api-management" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                      Bybit API Management
                    </a>
                  </p>
                </div>
              )}
            </div>

            {/* Bybit Instructions */}
            <div className="bg-orange-500/10 border border-orange-500 p-6 rounded-lg">
              <h3 className="font-heading text-lg font-bold text-orange-500 mb-3">📋 Как работает Bybit</h3>
              <ol className="space-y-2 text-sm">
                <li>1. Пользователь создаёт заявку → получает ваш Bybit адрес</li>
                <li>2. Средства поступают на ваш Bybit аккаунт</li>
                <li>3. Автоматический обмен и вывод (если включён)</li>
              </ol>
            </div>
            </div>
            
            {/* WhiteBIT Section */}
            <div className="border border-green-500/30 rounded-lg p-6 mt-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-heading text-xl font-bold text-green-500 uppercase">
                  🟢 WhiteBIT Integration
                </h2>
                <div className="flex gap-2">
                  <Button
                    onClick={fetchWhitebitBalance}
                    disabled={!whitebitConfigured}
                    className="bg-green-500 text-white hover:bg-green-600 disabled:opacity-50"
                  >
                    <Wallet size={16} className="mr-2" />
                    Баланс WhiteBIT
                  </Button>
                </div>
              </div>
              
              {!whitebitConfigured ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="mb-2">WhiteBIT API ключи не настроены</p>
                  <p className="text-sm">Добавьте WHITEBIT_API_KEY и WHITEBIT_API_SECRET в .env файл</p>
                </div>
              ) : (
                <>
                  {whitebitBalance && (
                    <div className="bg-card border border-border p-4 mb-4">
                      <h3 className="font-bold mb-3">💰 Баланс WhiteBIT</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {Object.entries(whitebitBalance).filter(([_, val]) => parseFloat(val?.available || 0) > 0).slice(0, 8).map(([coin, data]) => (
                          <div key={coin} className="bg-muted p-3 border border-border rounded">
                            <div className="font-mono font-bold text-green-500">{coin}</div>
                            <div className="font-mono text-lg">{parseFloat(data?.available || 0).toFixed(4)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-green-500/10 border border-green-500 p-6 rounded-lg">
                    <h3 className="font-heading text-lg font-bold text-green-500 mb-3">📋 Как работает WhiteBIT</h3>
                    <ol className="space-y-2 text-sm">
                      <li>1. Переключите биржу на WhiteBIT в панели заявок</li>
                      <li>2. Курсы будут синхронизироваться с WhiteBIT</li>
                      <li>3. Автоматический обмен будет через WhiteBIT API</li>
                    </ol>
                    <p className="mt-3 text-xs text-muted-foreground">
                      WhiteBIT используется как резервная биржа. Переключайтесь когда Bybit недоступен.
                    </p>
                  </div>
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* User Bybit API Keys Modal */}
      {showUserBybitModal && selectedUserForBybit && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowUserBybitModal(false)}>
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold text-orange-500">
                🔑 Bybit API для {selectedUserForBybit.email}
              </h3>
              <button onClick={() => setShowUserBybitModal(false)} className="text-muted-foreground hover:text-foreground">
                ✕
              </button>
            </div>
            
            <div className={`p-3 rounded mb-4 ${userBybitConfigured ? 'bg-green-500/20 text-green-500' : 'bg-muted text-muted-foreground'}`}>
              {userBybitConfigured ? '✓ API ключи настроены' : '✗ API ключи не настроены'}
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">
              Индивидуальные Bybit API ключи для этого пользователя. 
              Если настроены — будут использоваться вместо общих ключей.
            </p>
            
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">API Key</label>
                <Input
                  type="text"
                  value={userBybitApiKey}
                  onChange={(e) => setUserBybitApiKey(e.target.value)}
                  placeholder="Введите Bybit API Key"
                  className="bg-input border-border"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">API Secret</label>
                <Input
                  type="password"
                  value={userBybitApiSecret}
                  onChange={(e) => setUserBybitApiSecret(e.target.value)}
                  placeholder="Введите Bybit API Secret"
                  className="bg-input border-border"
                />
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button onClick={saveUserBybitKeys} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white">
                Сохранить
              </Button>
              {userBybitConfigured && (
                <Button onClick={deleteUserBybitKeys} variant="outline" className="text-red-500 border-red-500 hover:bg-red-500/10">
                  Удалить
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* User Bybit Analytics Modal */}
      {showBybitAnalytics && userBybitAnalytics && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowBybitAnalytics(false)}>
          <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-6xl w-full max-h-[95vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-slate-900 p-4 border-b border-slate-700 flex items-center justify-between z-10">
              <h3 className="font-heading text-xl font-bold text-orange-500">
                📊 Аналитика персональных Bybit API
              </h3>
              <button onClick={() => setShowBybitAnalytics(false)} className="text-slate-400 hover:text-white text-2xl">
                ✕
              </button>
            </div>
            
            {/* Summary Cards */}
            {userBybitAnalytics.summary && (
              <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 border-b border-slate-700">
                <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-emerald-400">${userBybitAnalytics.summary.total_profit?.toLocaleString()}</div>
                  <div className="text-xs text-slate-400 mt-1">Общая прибыль</div>
                </div>
                <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-blue-400">${userBybitAnalytics.summary.total_volume?.toLocaleString()}</div>
                  <div className="text-xs text-slate-400 mt-1">Общий объём</div>
                </div>
                <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-purple-400">${userBybitAnalytics.summary.global_monthly_forecast?.toLocaleString()}</div>
                  <div className="text-xs text-slate-400 mt-1">📈 Прогноз на месяц</div>
                </div>
                <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-amber-400">{userBybitAnalytics.users_count}</div>
                  <div className="text-xs text-slate-400 mt-1">Аккаунтов</div>
                </div>
              </div>
            )}
            
            {/* Comparison Table */}
            <div className="p-4 border-b border-slate-700">
              <h4 className="font-bold text-white mb-3">📋 Сравнение аккаунтов</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-slate-400 border-b border-slate-600">
                      <th className="text-left py-2 px-2">Пользователь</th>
                      <th className="text-right py-2 px-2">Заявок</th>
                      <th className="text-right py-2 px-2">Завершено</th>
                      <th className="text-right py-2 px-2">Конверсия</th>
                      <th className="text-right py-2 px-2">Объём $</th>
                      <th className="text-right py-2 px-2">Прибыль $</th>
                      <th className="text-right py-2 px-2">Ср. чек $</th>
                      <th className="text-right py-2 px-2">Ср. время</th>
                      <th className="text-right py-2 px-2">Прогноз мес.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userBybitAnalytics.users.map((user, idx) => (
                      <tr key={user.user_id} className={`border-b border-slate-700/50 ${idx === 0 ? 'bg-amber-500/10' : ''}`}>
                        <td className="py-2 px-2">
                          <div className="font-medium text-white">{user.telegram || user.email?.split('@')[0]}</div>
                          <div className="text-xs text-slate-500">{user.api_key_preview}</div>
                        </td>
                        <td className="py-2 px-2 text-right text-slate-300">{user.stats?.total_requests || 0}</td>
                        <td className="py-2 px-2 text-right text-green-400">{user.stats?.completed || 0}</td>
                        <td className="py-2 px-2 text-right">
                          <span className={`${user.stats?.conversion_rate >= 80 ? 'text-green-400' : user.stats?.conversion_rate >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                            {user.stats?.conversion_rate || 0}%
                          </span>
                        </td>
                        <td className="py-2 px-2 text-right text-blue-400 font-mono">${(user.stats?.volume_usd || 0).toLocaleString()}</td>
                        <td className="py-2 px-2 text-right text-emerald-400 font-bold">${(user.stats?.profit || 0).toFixed(2)}</td>
                        <td className="py-2 px-2 text-right text-slate-300">${user.stats?.avg_check || 0}</td>
                        <td className="py-2 px-2 text-right text-slate-300">{user.stats?.avg_processing_time_min || 0} мин</td>
                        <td className="py-2 px-2 text-right text-purple-400 font-bold">${user.stats?.monthly_forecast || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Individual User Cards with Charts */}
            <div className="p-4 space-y-4">
              <h4 className="font-bold text-white mb-3">📈 Графики прибыли по дням (30 дней)</h4>
              {userBybitAnalytics.users.length === 0 ? (
                <div className="text-center text-slate-400 py-8">
                  Нет пользователей с персональными Bybit API ключами
                </div>
              ) : (
                userBybitAnalytics.users.map((user) => (
                  <div key={user.user_id} className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="font-bold text-white text-lg">{user.telegram || user.email}</div>
                        <div className="text-sm text-slate-400">
                          API: {user.api_key_preview} • Прибыль за 7д: <span className="text-emerald-400">${user.stats?.profit_7d || 0}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-purple-400">${user.stats?.monthly_forecast || 0}</div>
                        <div className="text-xs text-slate-500">прогноз/мес</div>
                      </div>
                    </div>
                    
                    {/* Mini Chart - Simple bar chart with CSS */}
                    {user.daily_chart && user.daily_chart.length > 0 && (
                      <div className="mt-3">
                        <div className="flex items-end gap-1 h-24 bg-slate-700/30 rounded p-2">
                          {user.daily_chart.slice(-14).map((day, idx) => {
                            const maxProfit = Math.max(...user.daily_chart.slice(-14).map(d => d.profit || 0), 1);
                            const height = ((day.profit || 0) / maxProfit) * 100;
                            return (
                              <div key={idx} className="flex-1 flex flex-col items-center justify-end group relative">
                                <div 
                                  className="w-full bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t transition-all hover:from-emerald-400 hover:to-emerald-300"
                                  style={{ height: `${Math.max(height, 2)}%` }}
                                  title={`${day.date}: $${day.profit}`}
                                />
                                <div className="absolute -top-8 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-xs hidden group-hover:block whitespace-nowrap z-10">
                                  {day.date?.slice(5)}: ${day.profit}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex justify-between text-xs text-slate-500 mt-1">
                          <span>{user.daily_chart.slice(-14)[0]?.date?.slice(5)}</span>
                          <span>Последние 14 дней</span>
                          <span>{user.daily_chart.slice(-1)[0]?.date?.slice(5)}</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Stats Row */}
                    <div className="grid grid-cols-5 gap-2 mt-4">
                      <div className="bg-slate-700/50 rounded p-2 text-center">
                        <div className="text-lg font-bold text-white">{user.stats?.completed || 0}</div>
                        <div className="text-[10px] text-slate-400">Завершено</div>
                      </div>
                      <div className="bg-slate-700/50 rounded p-2 text-center">
                        <div className="text-lg font-bold text-blue-400">${(user.stats?.volume_usd || 0).toLocaleString()}</div>
                        <div className="text-[10px] text-slate-400">Объём</div>
                      </div>
                      <div className="bg-slate-700/50 rounded p-2 text-center">
                        <div className="text-lg font-bold text-emerald-400">${(user.stats?.profit || 0).toFixed(2)}</div>
                        <div className="text-[10px] text-slate-400">Прибыль</div>
                      </div>
                      <div className="bg-slate-700/50 rounded p-2 text-center">
                        <div className="text-lg font-bold text-amber-400">${user.stats?.avg_check || 0}</div>
                        <div className="text-[10px] text-slate-400">Ср. чек</div>
                      </div>
                      <div className="bg-slate-700/50 rounded p-2 text-center">
                        <div className="text-lg font-bold text-cyan-400">{user.stats?.avg_processing_time_min || 0}м</div>
                        <div className="text-[10px] text-slate-400">Ср. время</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;